import {
    BlurEvent, ChangeEvent, DataSource, DataSourceAction, DataSourceExecutionEvent, LookupModelSelectionEvent
} from "@mcleod/components";
import { Api, DatePart, DateUtil, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutCallinEntry } from "./autogen/AutogenLayoutCallinEntry";

export class CallinEntry extends AutogenLayoutCallinEntry {
    private sourceCallinLoaded: boolean = false;
    private sourceDispatchLoaded: boolean = false;

    calculateEta: (dataSource: DataSource, filter: any) => void;

    override onLoad(): void {
        this.textboxCurrentLocationTime.visible = false;
        this.citystateCurrentLocation.textCombined.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            this.cityStateSelected(event.selection);
        });
        this.sourceCallin.addBeforeExecutionListener((event: DataSourceExecutionEvent) => {
            if (event.getAction() == DataSourceAction.UPDATE) {
                const tz = this.getTZFromDate(new Date());
                this.sourceCallin.activeRow.set("local_timezone", tz);
            }
        });
    }

    cityStateSelected(selectedRow: ModelRow<any>) {
        const cityId = selectedRow?.get("id");
        if (cityId) {
            const d = new Date(this.textboxCallDateTime.getDataValue());
            const tz = this.getTZFromDate(d);
            Api.search("lme/general/calculate-timezone", { "city_id": cityId, "local_timezone": tz, "date_time": d }).then(result => {
                const data = result?.data[0];
                if (data) {
                    const calculatedDateTime = data["calculated_date_time"];
                    const calculatedTimezone = data["calculated_timezone"];
                    if (calculatedDateTime && data["timezones_equal"] != null && data["timezones_equal"] === false) {
                        this.textboxCurrentLocationTime.visible = true;
                        const formattedDate = DateUtil.formatDateTime(DateUtil.parseDateTime(calculatedDateTime), "iiii, MMMM dd h:mm a");
                        this.textboxCurrentLocationTime.text = formattedDate + " " + calculatedTimezone;
                    }
                    else
                        this.textboxCurrentLocationTime.visible = false;
                }
            });
            this.calcNextStopEta();
        }
    }

    switchCallinTypeOnChange(event: ChangeEvent) {
        this.citystateCurrentLocation.required = !event.newValue;
        if (event.newValue) {
            this.citystateCurrentLocation.validate(); //Since you start in this field and leave to change type, will be red when you leave field.
        }
    }

    compareNextSchedToCallinDate() {
        const callinDate = DateUtil.parseDateWithKeywords(this.textboxCallDateTime.text, true, true);
        const nextSchedDate = new Date(this.sourceDispatch.activeRow.get("next_sched_call"));
        const diffDays = DateUtil.dateDiff(DatePart.DAY, callinDate, nextSchedDate);
        const diffHours = DateUtil.dateDiff(DatePart.HOUR, callinDate, nextSchedDate);
        const diffMinutes = DateUtil.dateDiff(DatePart.MINUTE, callinDate, nextSchedDate);
        if (diffMinutes > 0) {
            if (diffMinutes < 60)
                this.labelCallWarning.text = "Check call is " + diffMinutes + " minutes past due";
            else if (diffHours < 24)
                this.labelCallWarning.text = "Check call is " + diffHours + " hours " + (diffMinutes % 60) + " minutes past due";
            else if (diffDays > 0)
                this.labelCallWarning.text = "Check call is " + diffDays + " days " + (diffHours % 24) + " hours " + (diffMinutes % 60) + " minutes past due";

            this.panelCallinWarning.visible = true;
        }
        else
            this.panelCallinWarning.visible = false;
    }

    /** This is an event handler for the afterExecution event of sourceDispatch.  */
    sourceDispatchAfterExecution() {
        this.compareNextSchedToCallinDate();
        if (StringUtil.isEmptyString(this.sourceDispatch.activeRow.get("override_driver2_nm"))){    
            this.panelDriverTwo.visible = false;
        }
        const showTempFields = this.sourceDispatch.activeRow.getBoolean("temp_range_set", false);
        this.textboxSetpointTemp.visible = this.textboxTemperature.visible = showTempFields;
        this.sourceDispatchLoaded = true;
        return this.syncCallinWithMove();
    }

    sourceCallinAfterExecution() {
        this.sourceCallinLoaded = true;
        this.labelScheduledCallTimeTMZ.text = this.labelCallTimeTMZ.text = this.labelNextTMZ.text = this.getTZFromDate(new Date());
        return this.syncCallinWithMove();
    }

    async syncCallinWithMove() {
        if (this.sourceCallinLoaded && this.sourceDispatchLoaded) {
            this.sourceCallin.activeRow.set("sched_date_time", this.sourceDispatch.activeRow.get("next_sched_call"));
            if (this.sourceCallin.activeRow.isNull("sched_date_time")) {
                this.panelScheduledCallTime.visible = false;
            }
            if (this.sourceDispatch.activeRow.isNull("checkcall_sched_id")) {
                this.sourceDispatch.activeRow.set("next_sched_call", null);
            }
            return this.updateNextSchedCall();
        }
    }

    updateNextSchedCall() {
        if (!this.sourceDispatch.activeRow.isNull("checkcall_sched_id")
        && !this.sourceDispatch.activeRow.isNull("origin_stop_id")
        && !this.sourceCallin.activeRow.isNull("call_date_time")) {
            return this.getNextScheduleCall(
                this.sourceDispatch.activeRow.get("checkcall_sched_id"),
                this.sourceDispatch.activeRow.get("origin_stop_id"),
                this.sourceCallin.activeRow.get("call_date_time")
            );
        }
    }
    
    getNextScheduleCall(scheduleId: string, stopId: string, callTime) {
        return Api.post("lme/dispatch/next-checkcall", { 
            "schedule_id": scheduleId, 
            "stop_id": stopId,
            "call_time": callTime 
        }).then(result => {
            const data = result?.data[0];
            if (data) {
                this.sourceDispatch.activeRow?.set("next_sched_call", data["next_sched_call"]);
            }
        });
    }

    textboxCallDateTimeOnBlur(event: BlurEvent) {
        this.calcNextStopEta();
        if (event.changedWhileFocused) {
            this.updateNextSchedCall();
        }
    }

    calcNextStopEta() {
        const moveId = this.sourceDispatch.activeRow.get("id");
        const cityId = this.citystateCurrentLocation?.textCombined?.getFirstLookupModelData()?.get("id") ?? this.sourceCallin.activeRow.get("city_id");
        const filter = { ...this.sourceCallin.activeRow.data, calc_miles_eta: true, movement_id: moveId, city_id: cityId };
        if (this.calculateEta != null)
            this.calculateEta(this.sourceCallin, filter);
    }

    bindCallinDataSource(callin: DataSource) {
        this.sourceCallin.rebindComponentsTo(callin);
        callin.attachListeners(this.sourceCallin);
        this.sourceCallin = callin;
        this.layoutCallinScript.sourceCallin = this.sourceCallin;
        this.layoutCallinScript.sourceCallinStop = this.sourceCallinStop;
    }

    private getTZFromDate(date: Date): string {
        return date.toLocaleString('en-US', { timeZoneName: 'short' }).split(' ').pop();
    }
}
